/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import MsDyn365, {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext,
    isChannelTypeB2B
} from '@msdyn365-commerce/core';
import {
    CatalogsDataActions,
    ChannelReferenceAdditionalLookupCriteria,
    ChannelReferenceLookupCriteria,
    ExtensibleEnumeration,
    FeatureState,
    ProductDeliveryOptions,
    ProductsDataActions,
    SalesLine,
    SalesOrder,
    SalesOrdersDataActions,
    SearchLocation,
    SimpleProduct,
    StoreOperationsDataActions,
    TransactionType
} from '@msdyn365-commerce/retail-proxy';
import {
    getSalesOrderDetailsByChannelReferenceIdAction,
    GetSalesOrderDetailsByConfirmationInput
} from './get-sales-order-details-by-confirmation-id';
import {
    ArrayExtensions,
    getSimpleProducts,
    ObjectExtensions,
    ProductInput,
    QueryResultSettingsProxy
} from '@msdyn365-commerce-modules/retail-actions';
import {
    getFeatureStatesAsync,
    getOnlineChannelAsync
} from '@msdyn365-commerce/retail-proxy/dist/DataActions/StoreOperationsDataActions.g';

const guestOrderLookupStorageKey = 'dobbiesGuestOrderLookup';
const authOrderLookupStorageKey = 'dobbiesOrderLookup';

interface IAuthLookupPayload {
    salesId: string;
    transactionId: string;
}

export interface ISalesOrderWithHydrationsSession {
    salesOrder: SalesOrder;
    products: SimpleProduct[];
    productDeliveryOptions?: ProductDeliveryOptions[];
    isSameSiteType?: boolean;
}

/**
 * Product and Catalog Interface.
 */
interface IProductCatalog {
    productId: number;
    catalogId: number | undefined;
}

/**
 * AdditionalLookupCriteria entity interface.
 */
export interface IAdditionalLookupCriteria {
    orderStatusLookUpParameterTypeName: string;
    orderStatusLookUpParameterValue: string;
}

interface IGuestLookupPayload {
    confirmationId: string;
    propertyName: string;
    propertyValue: string;
    additionalLookupCriteria?: IAdditionalLookupCriteria[];
}

/**
 * OrderTypes Types of orders.
 */
export enum orderTypes {
    salesOrder = 'salesOrder',
    transaction = 'transaction'
}

const getGuestLookupPayload = (): IGuestLookupPayload | undefined => {
    if (!MsDyn365.isBrowser) {
        return undefined;
    }

    try {
        const payloadJson = window.sessionStorage.getItem(guestOrderLookupStorageKey);
        if (!payloadJson) {
            return undefined;
        }

        const payload = JSON.parse(payloadJson) as IGuestLookupPayload;
        if (!payload?.confirmationId) {
            return undefined;
        }

        return payload;
    } catch {
        return undefined;
    }
};

const getAuthLookupPayload = (): IAuthLookupPayload | undefined => {
    if (!MsDyn365.isBrowser) {
        return undefined;
    }

    try {
        const payloadJson = window.sessionStorage.getItem(authOrderLookupStorageKey);
        if (!payloadJson) {
            return undefined;
        }

        const payload = JSON.parse(payloadJson) as IAuthLookupPayload;
        if (!payload?.salesId && !payload?.transactionId) {
            return undefined;
        }

        return payload;
    } catch {
        return undefined;
    }
};

/**
 * GetQueryStringParameters.
 * @param context -- URL.
 * @returns Additional lookup criteria -- IAdditionalLookupCriteria[].
 */
const getQueryStringParameters = (context: IActionContext): IAdditionalLookupCriteria[] => {
    const addtionalLookupParameter: IAdditionalLookupCriteria[] = [];
    const query = context.requestContext.query;

    if (!query) {
        return addtionalLookupParameter;
    }

    const queryArray = Object.entries(query);

    for (let index = 1; index <= queryArray.length; index++) {
        const additionalFieldName: string = `field${index}Name`;
        const additionalFieldValue = `field${index}Value`;
        if (additionalFieldName in query && additionalFieldValue in query) {
            addtionalLookupParameter.push({
                orderStatusLookUpParameterTypeName: query[additionalFieldName],
                orderStatusLookUpParameterValue: query[additionalFieldValue]
            });
        }
    }

    return addtionalLookupParameter;
};

/**
 * Calls the Retail API and returns the sales order for anonymous user.
 * @param confirmationId -- Confirmation Id.
 * @param propertyName -- Lookup property name.
 * @param propertyValue -- Lookup property value.
 * @param additionalLookupParameter -- Additional lookup parameters.
 * @returns SalesOrder -- Promise<SalesOrder>.
 */
const getSalesOrderDetailsByLookupCriteria = (
    confirmationId: string,
    propertyName: string,
    propertyValue: string,
    additionalLookupParameter: IAdditionalLookupCriteria[]
) => async (context: IActionContext): Promise<SalesOrder> => {
    const getExtensibleEnumerations = await StoreOperationsDataActions.getExtensibleEnumerationsAsync({ callerContext: context });

    const orderLookupExtensibleEnum: ExtensibleEnumeration[] =
        getExtensibleEnumerations.find(item => item.TypeName === 'OrderLookupAdditionalCriteriaType')?.ExtensibleEnumerations ?? [];

    const channelReferenceAdditionalLookupCriteria: ChannelReferenceAdditionalLookupCriteria[] = [];

    for (const item of orderLookupExtensibleEnum) {
        const retailProxyValue: string = item.Name.toLocaleLowerCase();
        const retailProxyTypeValue: number = item.Value;
        const orderLookupSearchPropertyName: string = propertyName.toLocaleLowerCase();
        if (retailProxyValue === orderLookupSearchPropertyName) {
            channelReferenceAdditionalLookupCriteria.push({
                OrderLookupAdditionalCriteriaTypeValue: item.Value,
                OrderLookupAdditionalCriteriaTypeParameterValue: propertyValue
            });
        }

        if (ArrayExtensions.hasElements(additionalLookupParameter)) {
            for (const additionalItem of additionalLookupParameter) {
                if (retailProxyValue === additionalItem.orderStatusLookUpParameterTypeName.toLocaleLowerCase()) {
                    channelReferenceAdditionalLookupCriteria.push({
                        OrderLookupAdditionalCriteriaTypeValue: retailProxyTypeValue,
                        OrderLookupAdditionalCriteriaTypeParameterValue: additionalItem.orderStatusLookUpParameterValue
                    });
                }
            }
        }
    }

    const channelReferenceLookupCriteria: ChannelReferenceLookupCriteria = {
        ChannelReferenceId: confirmationId,
        ChannelReferenceAdditionalLookupCriteria: channelReferenceAdditionalLookupCriteria
    };

    const input = new GetSalesOrderDetailsByConfirmationInput(channelReferenceLookupCriteria, context.requestContext.apiSettings);

    return getSalesOrderDetailsByChannelReferenceIdAction(input, context);
};

/**
 * Calls the Retail API and returns the sales order.
 * @param orderType -- Order Type.
 * @param orderId -- Order id.
 * @returns SalesOrder -- Promise<SalesOrder>.
 */
const getSalesOrder = (orderType: string = '', orderId: string = '') => async (context: IActionContext): Promise<SalesOrder> => {
    const { confirmationId = '', propertyName = '', propertyValue = '' } = context.requestContext.query ?? {};
    const queryAdditionalLookupParameter: IAdditionalLookupCriteria[] = getQueryStringParameters(context);

    const guestLookupPayload = context.requestContext.user.isAuthenticated ? undefined : getGuestLookupPayload();
    const effectiveConfirmationId = confirmationId || guestLookupPayload?.confirmationId || '';
    const effectivePropertyName = propertyName || guestLookupPayload?.propertyName || '';
    const effectivePropertyValue = propertyValue || guestLookupPayload?.propertyValue || '';
    const effectiveAdditionalLookupParameter = ArrayExtensions.hasElements(queryAdditionalLookupParameter)
        ? queryAdditionalLookupParameter
        : guestLookupPayload?.additionalLookupCriteria ?? [];

    if (effectiveConfirmationId) {
        return getSalesOrderDetailsByLookupCriteria(
            effectiveConfirmationId,
            effectivePropertyName,
            effectivePropertyValue,
            effectiveAdditionalLookupParameter
        )(context);
    }

    const effectiveOrderType =
        orderType ||
        (() => {
            const authPayload = getAuthLookupPayload();
            if (authPayload?.salesId) {
                return orderTypes.salesOrder;
            }
            if (authPayload?.transactionId) {
                return orderTypes.transaction;
            }
            return '';
        })();
    const effectiveOrderId = orderId || getAuthLookupPayload()?.salesId || getAuthLookupPayload()?.transactionId || '';

    if (effectiveOrderType === orderTypes.salesOrder) {
        return SalesOrdersDataActions.getSalesOrderDetailsBySalesIdAsync({ callerContext: context }, effectiveOrderId);
    }

    return SalesOrdersDataActions.getSalesOrderDetailsByTransactionIdAsync(
        { callerContext: context },
        effectiveOrderId,
        SearchLocation.All
    );
};

/**
 * Calls the Retail API and returns the products.
 * @param productCatalog - The product and catalog ids of the product to retrieve.
 * @param channelId - The channel id for the products.
 * @returns Simple product data.
 */
const getProductsWithCatalog = (productCatalog: IProductCatalog[], channelId?: number) => async (
    context: IActionContext
): Promise<SimpleProduct[]> => {
    const productInputs = productCatalog.map(
        index =>
            new ProductInput(
                index.productId,
                context.requestContext.apiSettings,
                channelId,
                undefined,
                context.requestContext,
                index.catalogId
            )
    );
    return getSimpleProducts(productInputs, context);
};

/**
 * GetSalesOrderWithHydrationsSession Input Action
 */
export class GetSalesOrderWithHydrationsSessionInput implements IActionInput {
    public orderType: string | undefined;

    public orderId: string | undefined;
    // TODO: Construct the input needed to run the action
    constructor(orderType: string | undefined, orderId: string | undefined) {
        this.orderType = orderType;
        this.orderId = orderId;
    }

    // TODO: Determine if the results of this get action should cache the results and if so provide
    // a cache object type and an appropriate cache key
    public getCacheKey = () => `SalesOrderWithHydrationsSession`;
    public getCacheObjectType = () => 'SalesOrderWithHydrationsSession';
    public dataCacheType = (): CacheType => 'request';
}

/**
 * TODO: Use this function to create the input required to make the action call
 */
const createInput = (args: ICreateActionContext): IActionInput => {
    return new GetSalesOrderWithHydrationsSessionInput(undefined, undefined);
};

/**
 * TODO: Use this function to call your action and process the results as needed
 */
async function action(input: GetSalesOrderWithHydrationsSessionInput, context: IActionContext): Promise<ISalesOrderWithHydrationsSession> {
    const { orderType, orderId } = input;
    const { confirmationId = '' } = context.requestContext.query ?? {};
    const guestLookupPayload = context.requestContext.user.isAuthenticated ? undefined : getGuestLookupPayload();
    const authLookupPayload = getAuthLookupPayload();

    if (
        context.requestContext.user.isAuthenticated &&
        (!orderType || !orderId) &&
        !confirmationId &&
        !guestLookupPayload?.confirmationId &&
        !authLookupPayload?.salesId &&
        !authLookupPayload?.transactionId
    ) {
        context.trace('[getSalesOrderWithHydrationsSessionAction] No orderType or orderId or confirmationId provided.');
        return {} as ISalesOrderWithHydrationsSession;
    }

    const salesOrder = await getSalesOrder(orderType, orderId)(context);
    if (salesOrder && salesOrder.SalesLines) {
        salesOrder.SalesLines = salesOrder.SalesLines.filter((saleline: SalesLine) => !saleline.IsVoided);
    }

    if (!salesOrder) {
        context.trace('[getSalesOrderWithHydrationsSessionAction] No salesOrder found.');
        return {} as ISalesOrderWithHydrationsSession;
    }

    if (salesOrder.TransactionTypeValue === TransactionType.SalesInvoice) {
        return {
            salesOrder,
            products: []
        };
    }
    let isEnabled: boolean = true;

    if (salesOrder.ChannelId !== context.requestContext.apiSettings.channelId && salesOrder.ChannelId !== undefined) {
        context.trace('[getSalesOrderWithHydrationsSessionAction] SalesOrder is not from current channel.');
        try {
            const channelInfo = await getOnlineChannelAsync({ callerContext: context }, salesOrder.ChannelId);
            const salesOrderChannelType = channelInfo?.OnlineStoreCustomerTypeValue;
            isEnabled = salesOrderChannelType === 1 && isChannelTypeB2B(context.requestContext);
        } catch (error) {
            console.log('Please check the channel type if it is not online, 400 expected.', error);
        }
    }

    if (!ArrayExtensions.hasElements(salesOrder.SalesLines)) {
        context.trace('[getSalesOrderWithHydrationsSessionAction] No salesLine found.');
        return {} as ISalesOrderWithHydrationsSession;
    }

    const productCatalogIds: IProductCatalog[] = salesOrder.SalesLines.map(salesLine => {
        return {
            productId: salesLine.ProductId !== undefined ? salesLine.ProductId : 0,
            catalogId: salesLine.CatalogId
        };
    });

    if (!ArrayExtensions.hasElements(productCatalogIds)) {
        context.trace('[getSalesOrderWithHydrationsSessionAction] No products in saleLines found.');
        return {} as ISalesOrderWithHydrationsSession;
    }

    const featureStates = await getFeatureStatesAsync({ callerContext: context }, [
        'Dynamics.AX.Application.ChannelMultipleCatalogsFeature'
    ]);
    const isCatalogsFeatureEnabled =
        featureStates?.find((featureState: FeatureState) => featureState.Name === 'Dynamics.AX.Application.ChannelMultipleCatalogsFeature')
            ?.IsEnabled || false;

    let products: SimpleProduct[] = [];

    if (!isCatalogsFeatureEnabled) {
        products = await getProductsWithCatalog(productCatalogIds, salesOrder.ChannelId)(context);
    } else {
        const userCatalogIds = await CatalogsDataActions.getCatalogsAsync(
            { callerContext: context },
            context.requestContext.apiSettings.channelId,
            true
        );

        const matchingProductCatalogIds = productCatalogIds.filter(productCatalogId => {
            return userCatalogIds.some(userCatalogId => userCatalogId.RecordId === productCatalogId.catalogId);
        });

        const nonMatchingProductCatalogIds = productCatalogIds.filter(productCatalogId => {
            return userCatalogIds.some(userCatalogId => userCatalogId.RecordId !== productCatalogId.catalogId);
        });

        let matchingProducts: SimpleProduct[] = [];
        if (matchingProductCatalogIds.length !== 0) {
            matchingProducts = await getProductsWithCatalog(
                matchingProductCatalogIds,
                context.requestContext.apiSettings.channelId
            )(context);
        }

        let nonMatchingProducts: SimpleProduct[] = [];
        if (nonMatchingProductCatalogIds.length !== 0) {
            nonMatchingProducts = await getProductsWithCatalog(nonMatchingProductCatalogIds, salesOrder.ChannelId)(context);
        }

        products = [...matchingProducts, ...nonMatchingProducts];
    }

    if (!ArrayExtensions.hasElements(products)) {
        context.trace('[getSalesOrderWithHydrationsSessionAction] No simple products found.');
        return {} as ISalesOrderWithHydrationsSession;
    }

    const validProducts = products.filter(product => !ObjectExtensions.isNullOrUndefined(product.RecordId));

    const deliveryOptions = await ProductsDataActions.getDeliveryOptionsAsync(
        {
            callerContext: context,
            queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(context)
        },
        validProducts.map(product => product.RecordId),
        {},
        4
    )
        .then(result => {
            return result;
        })
        .catch((error: Error) => {
            context.trace(error.message);
            context.telemetry.exception(error);
            context.telemetry.debug('[getDeliveryOptionsForCartLineItems]Error executing action');
            throw new Error('[getDeliveryOptionsForCartLineItems]Error executing action');
        });

    return {
        salesOrder,
        products,
        deliveryOptions,
        isSameSiteType: isEnabled
    } as ISalesOrderWithHydrationsSession;
}

export default createObservableDataAction({
    action: <IAction<ISalesOrderWithHydrationsSession>>action,
    // TODO: Give your data action a unique id
    id: 'GetSalesOrderWithHydrationsSession',
    input: createInput
    // TODO: Uncomment the below line if this is a meant to be a batched data action
    // isBatched: true
});
